import { useTranslation } from 'react-i18next';
import OpeningHoursSection from '../../assets/components/OpeningHoursSection';
import { OpeningHourEntry } from '../../../types/locations';
import type { StationDetailResponse } from '../../../api/station/stationApiTypes';
import { hoursOf } from '../stationLocationDraft';

interface Props {
    station: StationDetailResponse;
    mode: 'view' | 'edit';
    hours: OpeningHourEntry[];
    onHoursChange: (hours: OpeningHourEntry[]) => void;
    onValidityChange: (valid: boolean) => void;
}

/**
 * The location's opening hours, under their own heading (PO, 2026-10-19).
 *
 * They are the location's, shared by every room of a house and every station
 * at the address, and the section says so; a room shows them read-only and
 * points here. No buttons of its own: the page's one "Bearbeiten" opens it
 * with every other section, and its "Speichern" writes the hours with them.
 */
const StationOpeningHoursSection = ({ station, mode, hours, onHoursChange, onValidityChange }: Props) => {
    const { t } = useTranslation();
    // Without a location there are no hours; the location section says why.
    if (!station.location) return null;

    // ONE section: the shared editor brings its own heading and card, so the
    // hint goes into it rather than a second card around it (staging,
    // 2026-10-19: "Öffnungszeiten" appeared twice). A house is room family:
    // no hours at all means bookable around the clock, not closed.
    const hint = station.isHouse ? t('station_detail.hours.house_hint') : t('station_detail.hours.station_hint');
    return mode === 'edit' ? (
        <OpeningHoursSection
            hint={hint}
            openingHours={hours}
            onChange={onHoursChange}
            onValidityChange={onValidityChange}
            emptyMeansAlwaysOpen={station.isHouse}
        />
    ) : (
        <OpeningHoursSection
            hint={hint}
            mode={'view'}
            openingHours={hoursOf(station.location)}
            emptyMeansAlwaysOpen={station.isHouse}
        />
    );
};

export default StationOpeningHoursSection;
